import { AppAction } from "@edlab-fms/shared";
import { useMemo, useState } from "react";
import { useAuthQuery } from "../lib/api/auth";
import {
  useScholarshipAwardsQuery,
  useScholarshipFundsQuery,
  useStudentsQuery,
  type ScholarshipAwardListItem,
} from "../lib/api/scholarships";
import { formatMoney } from "../lib/format/money";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { MetricCard } from "../components/ui/MetricCard";
import { EmptyState } from "../components/ui/EmptyState";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { PageHeader } from "../components/ui/PageHeader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../components/ui/Table";
import { CreateAwardModal } from "../features/scholarships/CreateAwardModal";
import { CreateStudentModal } from "../features/scholarships/CreateStudentModal";
import { ScholarshipDetailModal } from "../features/scholarships/ScholarshipDetailModal";

function studentInitials(name: string): string {
  return name
    .split(" ")
    .slice(0, 2)
    .map((part) => part[0])
    .join("");
}

export function ScholarshipsPage() {
  const auth = useAuthQuery();
  const user = auth.data;
  const awards = useScholarshipAwardsQuery(Boolean(user));
  const students = useStudentsQuery(Boolean(user));
  const scholarshipFunds = useScholarshipFundsQuery(Boolean(user));

  const [awardOpen, setAwardOpen] = useState(false);
  const [awardStudentId, setAwardStudentId] = useState<string | undefined>();
  const [studentOpen, setStudentOpen] = useState(false);
  const [selected, setSelected] = useState<ScholarshipAwardListItem | null>(null);

  const canManage = user?.role === "super_admin" || user?.role === "accounts";
  const canRequestPayment = user?.permissions.actions.includes(AppAction.EXPENSE_CREATE);

  const summary = awards.data?.awards.reduce(
    (acc, award) => {
      acc.studentsWithAwards.add(award.studentId);
      acc.allocated += Number.parseFloat(award.allocatedAmount);
      acc.paid += Number.parseFloat(award.paidAmount);
      return acc;
    },
    { studentsWithAwards: new Set<string>(), allocated: 0, paid: 0 },
  ) ?? { studentsWithAwards: new Set<string>(), allocated: 0, paid: 0 };

  const registeredStudents = students.data?.students ?? [];

  const awardCountByStudent = useMemo(() => {
    const counts = new Map<string, number>();
    for (const award of awards.data?.awards ?? []) {
      counts.set(award.studentId, (counts.get(award.studentId) ?? 0) + 1);
    }
    return counts;
  }, [awards.data?.awards]);

  function openAwardModal(studentId?: string) {
    setAwardStudentId(studentId);
    setAwardOpen(true);
  }

  return (
    <div>
      <PageHeader
        eyebrow="Scholarships"
        title="A clear path from support to student."
        description="Track awards, payment requests and actual tuition payments."
        actions={
          canManage ? (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setStudentOpen(true);
                }}
              >
                Add student
              </Button>
              <Button
                onClick={() => {
                  openAwardModal();
                }}
              >
                Add scholarship
              </Button>
            </div>
          ) : undefined
        }
      />

      {registeredStudents.length > 0 || (awards.data?.awards.length ?? 0) > 0 ? (
        <div className="stats-grid mb-6 md:grid-cols-3 xl:grid-cols-4">
          <MetricCard
            label="Registered students"
            value={String(registeredStudents.length)}
            detail="Available for scholarship awards"
          />
          <MetricCard
            label="Students with awards"
            value={String(summary.studentsWithAwards.size)}
            detail="Linked to funding this period"
          />
          <MetricCard
            label="Total allocated"
            value={formatMoney(summary.allocated.toFixed(2))}
            detail="Award commitments"
          />
          <MetricCard
            label="Actually paid"
            value={formatMoney(summary.paid.toFixed(2))}
            detail="From linked payment vouchers"
          />
        </div>
      ) : null}

      {students.isLoading ? (
        <LoadingSpinner label="Loading students…" className="py-8" />
      ) : registeredStudents.length > 0 ? (
        <Card className="mb-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-edlab-ink">Registered students</h2>
              <p className="mt-1 text-sm text-edlab-muted">
                Add students here first, then create a scholarship award to allocate funding.
              </p>
            </div>
          </div>
          <Table caption="Registered scholarship students">
            <TableHead>
              <TableRow>
                <TableHeaderCell>Student</TableHeaderCell>
                <TableHeaderCell>Awards</TableHeaderCell>
                <TableHeaderCell> </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {registeredStudents.map((student) => {
                const awardCount = awardCountByStudent.get(student.id) ?? 0;
                return (
                  <TableRow key={student.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#dce7e3] text-xs font-semibold text-[#3c6557]">
                          {studentInitials(student.name)}
                        </span>
                        <span className="font-medium text-edlab-ink">{student.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {awardCount > 0 ? (
                        <span className="text-sm text-edlab-ink">
                          {awardCount} award{awardCount === 1 ? "" : "s"}
                        </span>
                      ) : (
                        <span className="text-sm text-edlab-muted">No award yet</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {canManage ? (
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={() => {
                            openAwardModal(student.id);
                          }}
                        >
                          {awardCount > 0 ? "Add award" : "Create award"}
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      ) : canManage ? (
        <div className="mb-6">
        <EmptyState
          title="No students registered yet"
          description="Add students to the scholarship registry, then create awards to link them to donor funds."
          action={
            <Button
              onClick={() => {
                setStudentOpen(true);
              }}
            >
              Add student
            </Button>
          }
        />
        </div>
      ) : null}

      {(awards.data?.awards.length ?? 0) > 0 ? (
        <h2 className="mb-3 text-lg font-semibold text-edlab-ink">Scholarship awards</h2>
      ) : null}

      {awards.isLoading ? (
        <LoadingSpinner label="Loading awards…" className="py-16" />
      ) : awards.isError ? (
        <EmptyState
          title="Could not load scholarships"
          description="Scholarships require access to the scholarship programme unit."
        />
      ) : awards.data?.awards.length ? (
        <Table caption="Scholarship awards">
          <TableHead>
            <TableRow>
              <TableHeaderCell>Student / institution</TableHeaderCell>
              <TableHeaderCell>Funding source</TableHeaderCell>
              <TableHeaderCell className="text-right">Allocated</TableHeaderCell>
              <TableHeaderCell className="text-right">Paid</TableHeaderCell>
              <TableHeaderCell className="text-right">Remaining</TableHeaderCell>
              <TableHeaderCell> </TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {awards.data.awards.map((award) => (
              <TableRow key={award.id}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#dce7e3] text-xs font-semibold text-[#3c6557]">
                      {studentInitials(award.studentName)}
                    </span>
                    <div>
                      <span className="block font-medium text-edlab-ink">{award.studentName}</span>
                      <span className="text-xs text-edlab-muted">
                        {award.institution} · {award.period}
                      </span>
                    </div>
                  </div>
                </TableCell>
                <TableCell>{award.fundName}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatMoney(award.allocatedAmount)}
                </TableCell>
                <TableCell className="text-right tabular-nums text-edlab-green">
                  {formatMoney(award.paidAmount)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatMoney(award.remainingAmount)}
                </TableCell>
                <TableCell>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => {
                      setSelected(award);
                    }}
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <EmptyState
          title="No scholarship awards yet"
          description="Create an award, then submit payment requests that flow into the expense workflow."
          action={
            canManage ? (
              <Button
                onClick={() => {
                  openAwardModal();
                }}
              >
                Add scholarship
              </Button>
            ) : undefined
          }
        />
      )}

      <p className="mt-6 text-xs text-edlab-muted">
        PKR · Payment requests do not count as paid until a payment voucher is recorded.
      </p>

      <CreateStudentModal
        open={studentOpen}
        onClose={() => {
          setStudentOpen(false);
        }}
      />

      <CreateAwardModal
        open={awardOpen}
        onClose={() => {
          setAwardOpen(false);
          setAwardStudentId(undefined);
        }}
        students={students.data?.students ?? []}
        funds={scholarshipFunds.data?.funds ?? []}
        fundsLoading={scholarshipFunds.isLoading}
        initialStudentId={awardStudentId}
      />

      <ScholarshipDetailModal
        award={selected}
        onClose={() => {
          setSelected(null);
        }}
        canRequestPayment={Boolean(canRequestPayment)}
      />
    </div>
  );
}
